import { attributeBindings, tagName } from "@ember-decorators/component"
import Component from "@ember/component"
import { computed, get, set } from "@ember/object"
import { scheduleOnce } from "@ember/runloop"
import type { AiValueEditor, RegisterAiValueEditor } from "cc-frontend/lib/ai/ai-value-editor"

@tagName("div")
@attributeBindings("contenteditable", "placeholder")
export default class SimpleContentEditable extends Component implements AiValueEditor<string> {
  declare $: any

  // Args (partial):
  isEditable = true
  value!: string
  placeholder = ""
  registerEditor?: RegisterAiValueEditor
  unregisterEditor?: () => void

  // Variables:
  _value?: string
  originalValue?: string
  focusInWordCount?: number

  isUserTyping = false
  isAiEditing = false
  plaintext = true

  // Properties:
  @computed("isEditable")
  get contenteditable() {
    var editable = this.isEditable
    return editable ? true : false
  }

  // Actions to receive
  onFocusIn = () => {}

  onFocusOut = () => {}
  onFinishedEditing: (value: string, originalValue: string, wordCountChange: number) => void =
    () => {}
  onValueUpdate: (value: string) => void = () => {}
  onClick = () => {}

  didInsertElement(): void {
    this.unregisterEditor = this.registerEditor?.(this)
  }

  willDestroyElement(): void {
    this.unregisterEditor?.()
  }

  didReceiveAttrs() {
    // @ts-ignore
    super.didReceiveAttrs(...arguments)
    if (get(this, "isAiEditing")) return
    if (get(this, "isUserTyping")) return
    if (get(this, "value") !== get(this, "_value")) {
      // @ts-ignore
      scheduleOnce("afterRender", () => this.$().text(get(this, "_value") || ""))
      set(this, "_value", get(this, "value"))
    }
  }

  mouseLeave() {
    if (this.isAiEditing) return

    this.set("isUserTyping", false)
  }

  focusIn() {
    if (this.isAiEditing) return

    var text = this.$().text()
    set(this, "originalValue", text)

    var focusInWordCount = 0
    // if there are words
    if (text.length > 0) {
      focusInWordCount = text.split(" ").length
    }

    this.set("focusInWordCount", focusInWordCount)
    this.set("isUserTyping", true)
    this.onFocusIn()
    return true
  }

  focusOut() {
    if (get(this, "isAiEditing")) return

    this.set("isUserTyping", false)
    this.onFocusOut()

    var text = this.$().text()
    let focusOutWordCount
    if (text.length > 0) focusOutWordCount = text.split(" ").length
    let wordCountChange = focusOutWordCount - this.focusInWordCount!

    if (text !== get(this, "originalValue")) {
      this.onFinishedEditing(text, get(this, "originalValue")!, wordCountChange)
    }

    // we've gotten value updates, so once we focus out, we set those
    set(this, "_value", get(this, "value"))

    // Ugh. This prevents a weird double text display where it looks like {{_value}}{{_value}}. Presumably,
    // Ember is getting confused by the text typed in and {{_value}}. This line straightens that out
    // @ts-ignore
    scheduleOnce("afterRender", () => {
      if (this.$()) this.$().text(get(this, "_value") || "")
    })

    return true
  }

  keyDown(event) {
    if (get(this, "isAiEditing")) return

    if (!event.metaKey) {
      this.set("isUserTyping", true)
    }
    // prevent enter key
    if (event.keyCode === 13 || event.which === 13) {
      event.preventDefault()
      return true
    }

    return true
  }

  keyUp(event) {
    if (get(this, "isAiEditing")) return

    // this.set('_value', this.$().text())
    this.onValueUpdate(this.$().text())
  }

  drop(event) {
    event.preventDefault()
  }

  click() {
    if (get(this, "isAiEditing")) return

    this.onClick()
  }

  paste(e) {
    if (get(this, "isAiEditing")) {
      e.preventDefault()
      return
    }

    var pastedText
    // @ts-ignore
    if (window.clipboardData && window.clipboardData.getData) {
      // IE
      // @ts-ignore
      pastedText = window.clipboardData.getData("URL")
    } else if (e.originalEvent.clipboardData && e.originalEvent.clipboardData.getData) {
      pastedText = e.originalEvent.clipboardData.getData("text/plain")
    }
    document.execCommand("insertHTML", false, pastedText)
    this.set("_value", this.$().text())
    this.onValueUpdate(this.$().text())

    e.preventDefault()
  }

  // AiValueEditor methods

  startAiEdits(): void {
    this.isAiEditing = true
  }

  setValueFromAi(aiValue: string): void {
    this.set("_value", aiValue)
    // Copied from focusOut().
    // If I don't call this, the display doesn't update (even though it re-renders with the new _value).
    // @ts-ignore
    scheduleOnce("afterRender", () => {
      if (this.$()) this.$().text(get(this, "_value") || "")
    })
  }

  acceptAiEdits(): string {
    const result = get(this, "_value") || ""
    this.isAiEditing = false
    // Copied from focusOut().
    // @ts-ignore
    scheduleOnce("afterRender", () => {
      if (this.$()) this.$().text(get(this, "_value") || "")
    })

    return result
  }

  rejectAiEdits(): void {
    if (!this.isAiEditing) return

    this.isAiEditing = false
    set(this, "_value", get(this, "value"))
    // Copied from focusOut().
    // @ts-ignore
    scheduleOnce("afterRender", () => {
      if (this.$()) this.$().text(get(this, "_value") || "")
    })
  }
}
